import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetBooksQuery } from "../../../redux-toolkit/features/books/bookApiSlice";
import { useGetAllCompaniesQuery } from "../../../redux-toolkit/features/company/companyApiSlice";
import {
  useGetPromotionsQuery,
  useGetPromotionStatusesQuery,
  useLazyGetPromotionsQuery,
} from "../../../redux-toolkit/features/promotions/promotionsApiSlice";
import { debounce } from "../../../utils";
import { useAlert } from "../../../hooks/useAlert";
import {
  DEFAULT_PROMOTIONS_DATE_FILTER_FIELD,
  buildHistoryDateQueryArgs,
} from "./promotionHistoryColumns";
import {
  collectPaginatedList,
  downloadCsvFile,
  mapCreateEventEligibilityToCsv,
  mapPromotionEligibilityToCsv,
} from "./promotionCsv";
import {
  CREATE_EVENT_VIEW,
  PROMOTIONS_LIST_MODES,
} from "./promotionListModes";
import { PROMOTION_STATUSES } from "../../../constants/promotionStatuses";

const sortOrgBooks = (list) =>
  [...list].sort((a, b) => (a.order || 0) - (b.order || 0));

/**
 * Shared eligibility roster state for Create Events + Promotions Reports.
 * @param {"create"|"report"} mode
 */
export const usePromotionsEligibilityList = (
  mode = PROMOTIONS_LIST_MODES.REPORT
) => {
  const isCreateMode = mode === PROMOTIONS_LIST_MODES.CREATE;
  const { displayAlert } = useAlert();
  const { data: booksData, isLoading: booksLoading } = useGetBooksQuery("org");
  const { data: companiesData } = useGetAllCompaniesQuery();
  const { data: statusesData } = useGetPromotionStatusesQuery(undefined, {
    skip: isCreateMode,
  });

  const books = useMemo(() => {
    const list = booksData?.data?.books || booksData?.data || [];
    return Array.isArray(list) ? sortOrgBooks(list) : [];
  }, [booksData]);

  const companies = useMemo(() => {
    const raw = companiesData?.data?.companies || companiesData?.data || [];
    return Array.isArray(raw) ? raw : [];
  }, [companiesData]);

  const statuses = useMemo(() => {
    const raw = statusesData?.data;
    return Array.isArray(raw) ? raw : [];
  }, [statusesData]);

  const [selectedBookId, setSelectedBookId] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [companyIds, setCompanyIds] = useState([]);
  const [status, setStatus] = useState("");
  const [dateField, setDateField] = useState(
    DEFAULT_PROMOTIONS_DATE_FILTER_FIELD
  );
  const [dateStart, setDateStart] = useState(null);
  const [dateEnd, setDateEnd] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [fetchPromotions] = useLazyGetPromotionsQuery();

  useEffect(() => {
    if (!selectedBookId && books.length > 0) {
      setSelectedBookId(books[0]._id);
    }
  }, [books, selectedBookId]);

  const setDebouncedSearch = useMemo(
    () => debounce((value) => setSearchQuery(value), 400),
    []
  );

  const onSearchChange = useCallback(
    (val) => {
      setSearchInput(val);
      setDebouncedSearch(val);
    },
    [setDebouncedSearch]
  );

  const onDateApply = useCallback(({ field, startDate, endDate }) => {
    setDateField(field);
    setDateStart(startDate);
    setDateEnd(endDate);
  }, []);

  const onDateReset = useCallback(() => {
    setDateField(DEFAULT_PROMOTIONS_DATE_FILTER_FIELD);
    setDateStart(null);
    setDateEnd(null);
  }, []);

  const queryArgs = useMemo(
    () => ({
      bookId: selectedBookId,
      companyIds: companyIds.length ? companyIds : undefined,
      ...(isCreateMode
        ? { view: CREATE_EVENT_VIEW }
        : { status: status || undefined }),
      search: searchQuery || undefined,
      ...buildHistoryDateQueryArgs({
        field: dateField,
        startDate: dateStart,
        endDate: dateEnd,
      }),
      page: 1,
      limit: 200,
    }),
    [
      selectedBookId,
      companyIds,
      isCreateMode,
      status,
      searchQuery,
      dateField,
      dateStart,
      dateEnd,
    ]
  );

  const { data, isLoading, isFetching } = useGetPromotionsQuery(queryArgs, {
    skip: !selectedBookId,
    refetchOnMountOrArgChange: true,
  });

  const promotions = useMemo(() => {
    const list = data?.data?.promotions?.list || [];
    if (!isCreateMode) return list;
    return list.map((row) => ({
      ...row,
      status: row.status || PROMOTION_STATUSES.CONTACT_PENDING,
    }));
  }, [data, isCreateMode]);

  const selectedBookName = useMemo(
    () => books.find((b) => b._id === selectedBookId)?.name || "",
    [books, selectedBookId]
  );

  const resetFilters = useCallback(() => {
    setSearchInput("");
    setSearchQuery("");
    setCompanyIds([]);
    setStatus("");
    onDateReset();
  }, [onDateReset]);

  const handleExport = useCallback(async () => {
    if (isExporting || !selectedBookId) return;
    setIsExporting(true);
    try {
      const list = await collectPaginatedList(
        (page, limit) =>
          fetchPromotions({ ...queryArgs, page, limit }).unwrap(),
        (result) => ({
          list: result?.data?.promotions?.list || [],
          pages: result?.data?.promotions?.totalPages || 1,
        })
      );
      if (!list.length) {
        displayAlert({
          open: true,
          message: "No promotions to export",
          type: "info",
          timeout: "2000",
        });
        return;
      }
      const bookName = (selectedBookName || "promotions")
        .replace(/\s+/g, "-")
        .toLowerCase();
      const mapRows = isCreateMode
        ? mapCreateEventEligibilityToCsv
        : mapPromotionEligibilityToCsv;
      const prefix = isCreateMode ? "promotions" : "promotions-report";
      downloadCsvFile(mapRows(list), `${prefix}-${bookName}.csv`);
    } catch (err) {
      displayAlert({
        open: true,
        message:
          err?.data?.message || err?.message || "Unable to export promotions",
        type: "error",
        timeout: "3000",
      });
    } finally {
      setIsExporting(false);
    }
  }, [
    isExporting,
    selectedBookId,
    fetchPromotions,
    queryArgs,
    displayAlert,
    selectedBookName,
    isCreateMode,
  ]);

  return {
    books,
    booksLoading,
    companies,
    statuses,
    selectedBookId,
    setSelectedBookId,
    selectedBookName,
    searchInput,
    searchQuery,
    onSearchChange,
    companyIds,
    setCompanyIds,
    status,
    setStatus,
    dateField,
    dateStart,
    dateEnd,
    onDateApply,
    onDateReset,
    resetFilters,
    promotions,
    isLoading,
    isFetching,
    isExporting,
    handleExport,
  };
};
